import { useState, useCallback } from "react";
import type { ValueItem } from "./types";

export function useAttributeValues(initialValues: ValueItem[] = []) {
  const [values, setValues] = useState<ValueItem[]>(initialValues);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleAddValue = useCallback(() => {
    const newId = String(Date.now());
    const maxOrder =
      values.length > 0 ? Math.max(...values.map((v) => v.order)) : 0;
    setValues([...values, { id: newId, value: "", order: maxOrder + 1 }]);
  }, [values]);

  const handleRemoveValue = useCallback(
    (id: string) => {
      if (values.length > 1) {
        setValues(values.filter((v) => v.id !== id));
      }
    },
    [values]
  );

  const handleValueChange = useCallback(
    (id: string, value: string) => {
      setValues(values.map((v) => (v.id === id ? { ...v, value } : v)));
    },
    [values]
  );

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) {
        setDragOverIndex(null);
        return;
      }
      setDragOverIndex(index);
    },
    [draggedIndex]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      const newValues = [...values];
      const draggedItem = newValues[draggedIndex];
      newValues.splice(draggedIndex, 1);
      newValues.splice(dropIndex, 0, draggedItem);
      setValues(newValues);
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [draggedIndex, values]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const resetDragState = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  return {
    values,
    setValues,
    draggedIndex,
    dragOverIndex,
    handleAddValue,
    handleRemoveValue,
    handleValueChange,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetDragState,
  };
}

